import { twMerge } from 'tailwind-merge';
import { isEmpty } from '@witmetrics/utils/misc';
import { fromBlankArray } from '@witmetrics/utils/arrays';
import OverviewCard from '@/components/OverviewCard';
import PercentageBar from '@/components/PercentageBar';
import Skeleton from '@/components/Skeleton';
import TruncatedText from '@/components/TruncatedText';
import type { TopTreatment } from '../types';
import { TOP_TREATMENTS_LIMIT } from '../constants';
import { SKELETON, STAGGER } from './constants';
import { classes } from './utils';

export type TopTreatmentsCardProps = {
  isFetchingData?: boolean;
  className?: string;
  fillClassName?: string;
  title: string;
  subtitle: string;
  treatments: TopTreatment[];
  formatValue: (value: number) => string;
};

export default function TopTreatmentsCard({
  isFetchingData,
  className,
  fillClassName,
  title,
  subtitle,
  treatments,
  formatValue,
}: TopTreatmentsCardProps) {
  const renderRow = ({ id, name, value, width }: TopTreatment, i: number) => (
    <div key={id} className={classes.row}>
      <span className={classes.rank}>{i + 1}</span>
      <TruncatedText className={classes.name} tooltipPlacement="top">
        {name}
      </TruncatedText>
      <PercentageBar
        className={classes.bar}
        fillClassName={fillClassName}
        width={width}
      />
      <span className={classes.value}>{formatValue(value)}</span>
    </div>
  );

  const renderPlaceholderRow = (i: number) => (
    <div key={i} className={classes.row}>
      <span className={classes.rank}>{i + 1}</span>
      <div className={classes.name}>
        <Skeleton delay={i * STAGGER} {...SKELETON.name} />
      </div>
      <PercentageBar className={classes.bar} width={0} />
      <div className={twMerge(classes.value, classes.valueSkeleton)}>
        <Skeleton delay={i * STAGGER} {...SKELETON.value} />
      </div>
    </div>
  );

  const renderBody = () => {
    if (isFetchingData) {
      return fromBlankArray(TOP_TREATMENTS_LIMIT, renderPlaceholderRow);
    }
    if (isEmpty(treatments)) {
      return <p className={classes.empty}>No accepted treatments this year</p>;
    }
    return treatments.map(renderRow);
  };

  return (
    <OverviewCard className={className} title={title} subtitle={subtitle}>
      <div className={classes.rows}>{renderBody()}</div>
    </OverviewCard>
  );
}
